/**
 * Пульс — нормальный ли сегодня день по всей воронке продаж.
 *
 * Неделя по дням со светофором по трём этапам (Meta → чаты → продажи), под ней — что не так в выбранном дне и детали по этапам.
 * Сутки — опер-день 06:00–06:00, как смены. Норма — медиана прошлых 7 дней
 * на тот же час (backend/src/marketing/pulse.ts).
 * Сегодняшний день обновляется раз в минуту.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import client from "../../api/client";
import CreativeModal, { CreativeDay } from "./CreativeModal";
import "./index.css";

type Status = "ok" | "warn" | "bad" | "none";

interface Kpi {
  value: number | null;
  cut: number | null;
  norm: number | null;
  status: Status;
}

interface Row {
  key: string;
  label: string;
  value: number;
  cut: number;
  norm: number;
  status: Status;
  extra?: Record<string, any>;
}

interface PulseDay {
  date: string;
  weekday: number;
  isToday: boolean;
  isFuture: boolean;
  cutHour: number;
  kpis: Record<string, Kpi>;
  sections: Record<"meta" | "chats" | "sales", Status>;
}

interface Pulse {
  weekStart: string;
  selected: string;
  today: string;
  nowHour: number;
  dayStartHour: number;
  chatsCalendarDays: boolean;
  days: PulseDay[];
  detail: {
    diagnosis: { section: string; status: Status; text: string }[];
    meta: {
      campaigns: Row[];
      creatives: Row[];
      creativeDays?: Record<string, CreativeDay[]>;
      currency: string;
    };
    chats: { channels: Row[]; operators: Row[] };
    sales: { markets: Row[]; managers: Row[]; channels: Row[] | null };
  };
  errors: Record<string, string>;
}

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
// kpi — главная цифра этапа в карточке дня.
const SECTIONS: { key: keyof PulseDay["sections"]; label: string; kpi: string; hint: string }[] = [
  { key: "meta", label: "Meta", kpi: "metaLeads", hint: "лидов" },
  { key: "chats", label: "Чаты", kpi: "chatsNew", hint: "первых контактов" },
  { key: "sales", label: "Продажи", kpi: "sales", hint: "продаж" },
];
const STATUS_TEXT: Record<Status, string> = { ok: "норма", warn: "ниже нормы", bad: "провал", none: "нет данных" };

function addDays(d: string, n: number): string {
  return new Date(Date.parse(`${d}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
}

const ddmm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
const clock = (h: number) => `${String(h % 24).padStart(2, "0")}:00`;

function fmt(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return v.toLocaleString("ru-RU", { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

function delta(cut: number | null, norm: number | null): string {
  if (cut === null || !norm) return "";
  const p = Math.round((cut / norm - 1) * 100);
  return p > 0 ? `+${p}%` : `${p}%`;
}

const Dot: React.FC<{ status: Status; title?: string }> = ({ status, title }) => (
  <span className={`pulse-dot pulse-dot--${status}`} title={title} />
);

const Skel: React.FC<{ w?: number | string; h?: number }> = ({ w = "100%", h = 12 }) => (
  <span className="pulse-skel" style={{ width: w, height: h }} />
);

const SkelTable: React.FC<{ title: string; rows?: number }> = ({ title, rows = 4 }) => (
  <div className="pulse-table">
    <div className="pulse-table__title">{title}</div>
    <div className="pulse-skel-rows">
      {Array.from({ length: rows }, (_, i) => (
        <Skel key={i} w={`${90 - i * 12}%`} />
      ))}
    </div>
  </div>
);

const Tile: React.FC<{
  title: string;
  status: Status;
  main: Kpi | undefined;
  mainLabel: string;
  digits?: number;
  lines: { label: string; kpi?: Kpi; digits?: number; suffix?: string; lowerIsBetter?: boolean }[];
  items?: { key: string; status: Status; label: string; value: React.ReactNode }[] | null; // null — ещё грузятся
  itemsTitle?: string;
  error?: string;
}> = ({ title, status, main, mainLabel, digits = 0, lines, items, itemsTitle, error }) => (
  <div className={`pulse-tile pulse-tile--${status}`}>
    <div className="pulse-tile__head">
      <span>{title}</span>
      <span className={`pulse-badge pulse-badge--${status}`}>{STATUS_TEXT[status]}</span>
    </div>
    {error ? (
      <div className="pulse-tile__error">{error}</div>
    ) : (
      <>
        <div className="pulse-tile__main">
          {fmt(main?.value, digits)}
          <span className="pulse-tile__label">{mainLabel}</span>
        </div>
        <div className="pulse-tile__norm">
          к этому часу {fmt(main?.cut, digits)} при норме {fmt(main?.norm, digits)}{" "}
          <b className={`pulse-delta pulse-delta--${main?.status || "none"}`}>{delta(main?.cut ?? null, main?.norm ?? null)}</b>
        </div>
        {lines.map((l) => (
          <div className="pulse-tile__line" key={l.label}>
            <span>{l.label}</span>
            <span>
              {fmt(l.kpi?.value, l.digits)}
              {l.suffix || ""} <span className="pulse-muted">норма {fmt(l.kpi?.norm, l.digits)}</span>
            </span>
          </div>
        ))}
        {items === null && itemsTitle && (
          <div className="pulse-tile__items">
            <div className="pulse-tile__items-title">{itemsTitle}</div>
            <Skel w="80%" />
            <Skel w="65%" />
            <Skel w="70%" />
          </div>
        )}
        {items && items.length > 0 && (
          <div className="pulse-tile__items">
            {itemsTitle && <div className="pulse-tile__items-title">{itemsTitle}</div>}
            {items.map((it) => (
              <div className="pulse-tile__item" key={it.key}>
                <Dot status={it.status} />
                <span className="pulse-tile__item-label">{it.label}</span>
                <span className="pulse-tile__item-value">{it.value}</span>
              </div>
            ))}
          </div>
        )}
      </>
    )}
  </div>
);

const Table: React.FC<{
  title: string;
  rows: Row[];
  cols: { label: string; render: (r: Row) => React.ReactNode; num?: boolean }[];
  empty?: string;
  onRowClick?: (r: Row) => void;
}> = ({ title, rows, cols, empty, onRowClick }) => (
  <div className="pulse-table">
    <div className="pulse-table__title">{title}</div>
    {rows.length === 0 ? (
      <div className="pulse-muted pulse-table__empty">{empty || "Нет данных"}</div>
    ) : (
      <div className="pulse-table__scroll">
        <table>
          <thead>
            <tr>
              <th />
              {cols.map((c) => (
                <th key={c.label} className={c.num ? "num" : ""}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                className={onRowClick ? "pulse-table__row--click" : undefined}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
              >
                <td className="pulse-table__dot">
                  <Dot status={r.status} />
                </td>
                {cols.map((c) => (
                  <td key={c.label} className={c.num ? "num" : ""}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// «к этому часу / норма (+x%)» — общая колонка для таблиц.
const vsNorm = (r: Row, digits = 0) => (
  <>
    {fmt(r.cut, digits)} <span className="pulse-muted">/ {fmt(r.norm, digits)}</span>{" "}
    <span className={`pulse-delta pulse-delta--${r.status}`}>{delta(r.cut, r.norm)}</span>
  </>
);

const PulsePage: React.FC = () => {
  // selected: "" — сегодня (сервер сам знает, какой сейчас опер-день).
  const [selected, setSelected] = useState<string>("");
  // frame — последний загруженный ответ: из него неделя и главные цифры
  // выбранного дня, пока его детали догружаются.
  const [data, setData] = useState<Pulse | null>(null);
  const cache = useRef(new Map<string, { at: number; pulse: Pulse }>());
  const [, rerender] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [openCreative, setOpenCreative] = useState<string | null>(null);

  const fetchDay = useCallback(async (date: string) => {
    const { data } = await client.get<Pulse>("/marketing/pulse", { params: date ? { date } : {} });
    cache.current.set(data.selected, { at: Date.now(), pulse: data });
    return data;
  }, []);

  const load = useCallback(
    async (date: string, silent = false) => {
      if (!silent) setLoading(true);
      try {
        const p = await fetchDay(date);
        setData(p);
        if (!date) setSelected(p.selected);
        setError("");
        setUpdatedAt(new Date());
        rerender((x) => x + 1);
        // Остальные прошедшие дни недели — заранее, чтобы клик открывал сразу.
        for (const d of p.days) {
          if (d.isFuture || cache.current.has(d.date)) continue;
          try {
            await fetchDay(d.date);
          } catch {
            break;
          }
        }
      } catch (e: any) {
        setError(e?.response?.data?.error || "Не удалось загрузить");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [fetchDay]
  );

  useEffect(() => {
    const hit = selected ? cache.current.get(selected) : undefined;
    if (hit) {
      setData(hit.pulse);
      // Сегодня живое — показали из кэша, тихо обновляем.
      if (hit.pulse.selected === hit.pulse.today && Date.now() - hit.at > 60_000) load(selected, true);
      return;
    }
    load(selected);
  }, [selected, load]);

  // Сегодня — живой день: обновляем раз в минуту.
  useEffect(() => {
    if (!data || selected !== data.today) return;
    const t = setInterval(() => load(selected, true), 60_000);
    return () => clearInterval(t);
  }, [data, selected, load]);

  if (!data) {
    return (
      <div className="pulse">
        {error ? (
          <div className="pulse-error">{error}</div>
        ) : (
          <>
            <h1>Пульс</h1>
            <div className="pulse-week">
              {Array.from({ length: 7 }, (_, i) => (
                <div key={i} className="pulse-day">
                  <Skel w="60%" h={14} />
                  <Skel />
                  <Skel />
                </div>
              ))}
            </div>
            <div className="pulse-tiles">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="pulse-tile">
                  <Skel w="40%" />
                  <Skel w="30%" h={28} />
                  <Skel />
                  <Skel w="70%" />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // Выбранный день другой недели — рамки ещё нет, держим старую неделю.
  const view = cache.current.get(selected)?.pulse || null;
  const sel = (view || data).days.find((d) => d.date === selected) || data.days.find((d) => d.date === data.selected)!;
  const ready = !!view;
  const k = sel.kpis;
  const start = data.dayStartHour;
  const cutText = sel.isToday
    ? sel.cutHour === 0
      ? `день только начался (с ${clock(start)})`
      : `сравнение ${clock(start)}–${clock(start + sel.cutHour)}`
    : `весь день ${clock(start)}–${clock(start)}`;
  const setDate = (d: string) => setSelected(d);
  const cur = data.detail.meta.currency;
  const errors = (view || data).errors;
  const { meta, chats, sales } = (view || data).detail;

  return (
    <div className="pulse">
      <div className="pulse-header">
        <h1>Пульс</h1>
        <div className="pulse-header__nav">
          <button onClick={() => setDate(addDays(data.weekStart, -7))}>‹ неделя</button>
          <span>
            {ddmm(data.weekStart)} – {ddmm(addDays(data.weekStart, 6))}
          </span>
          <button disabled={addDays(data.weekStart, 7) > data.today} onClick={() => setDate(addDays(data.weekStart, 7))}>
            неделя ›
          </button>
          {selected !== data.today && <button onClick={() => setDate(data.today)}>сегодня</button>}
        </div>
        <span className="pulse-muted">
          {loading || !ready ? "загружаю…" : updatedAt ? `обновлено ${updatedAt.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}` : ""}
        </span>
      </div>

      <div className="pulse-week">
        {data.days.map((d) => (
          <button
            key={d.date}
            disabled={d.isFuture}
            className={`pulse-day ${d.date === selected ? "pulse-day--active" : ""}`}
            onClick={() => setDate(d.date)}
          >
            <div className="pulse-day__date">
              {WEEKDAYS[d.weekday]} {ddmm(d.date)}
              {d.isToday && <span className="pulse-day__today">сегодня</span>}
            </div>
            <div className="pulse-day__dots">
              {SECTIONS.map((s) => {
                const kp = d.kpis[s.kpi];
                return (
                  <span
                    key={s.key}
                    className="pulse-day__dot"
                    title={`${s.label}: ${STATUS_TEXT[d.sections[s.key]]}${kp ? `, ${fmt(kp.value)} ${s.hint}, норма ${fmt(kp.norm)}` : ""}`}
                  >
                    <Dot status={d.isFuture ? "none" : d.sections[s.key]} />
                    <span>{s.label}</span>
                    {!d.isFuture && <b className="pulse-day__num">{fmt(kp?.value)}</b>}
                  </span>
                );
              })}
            </div>
          </button>
        ))}
      </div>

      <div className="pulse-diagnosis">
        <div className="pulse-diagnosis__title">
          {WEEKDAYS[sel.weekday]} {ddmm(sel.date)} · <span className="pulse-muted">{cutText}, норма — медиана 7 прошлых дней</span>
        </div>
        {ready ? (
          view!.detail.diagnosis.map((d, i) => (
            <div key={i} className="pulse-diagnosis__item">
              <Dot status={d.status} />
              <span>{d.text}</span>
            </div>
          ))
        ) : (
          <>
            <Skel w="55%" />
            <Skel w="40%" />
          </>
        )}
      </div>

      <div className="pulse-tiles">
        <Tile
          title="1. Meta Ads"
          status={sel.sections.meta}
          main={k.metaLeads}
          mainLabel="лидов"
          error={errors.meta}
          lines={[
            { label: "Клики", kpi: k.metaClicks },
            { label: `Спенд, ${cur}`, kpi: k.metaSpend },
            { label: `Цена лида, ${cur}`, kpi: k.metaCpl, digits: 2 },
          ]}
        />
        <Tile
          title="2. Чаты"
          status={sel.sections.chats}
          main={k.chatsNew}
          mainLabel="первых контактов"
          error={errors.chats}
          lines={[{ label: "Медиана ответа", kpi: k.chatsReply, digits: 1, suffix: " мин" }]}
          itemsTitle="Продажники — ответов, медиана"
          items={!ready ? null : chats.operators
            .filter((o) => o.value > 0 || o.extra?.absent)
            .map((o) => ({
              key: o.key,
              status: o.extra?.absent ? "bad" : o.status,
              label: o.label,
              value: o.extra?.absent ? (
                <span className="pulse-muted">не работает</span>
              ) : (
                <>
                  {fmt(o.value)} <span className="pulse-muted">· {fmt(o.extra?.medianMin, 1)} мин</span>
                </>
              ),
            }))}
        />
        <Tile
          title="3. Продажи"
          status={sel.sections.sales}
          main={k.sales}
          mainLabel="продаж"
          error={errors.sales}
          lines={
            k.chatsBought
              ? [{ label: "Купили из первых контактов дня", kpi: k.chatsBought }]
              : []
          }
          itemsTitle="Каналы чатов — купили / норма"
          items={!ready ? null : (sales.channels || []).map((m) => ({
            key: m.key,
            status: m.status,
            label: m.label,
            value: (
              <>
                {fmt(m.value)} <span className="pulse-muted">/ {fmt(m.norm)}</span>
              </>
            ),
          }))}
        />
      </div>

      {!ready ? (
        <div className="pulse-details">
          <section>
            <h2>Meta Ads</h2>
            <SkelTable title="Кампании — лиды" rows={5} />
            <SkelTable title="Креативы" rows={6} />
          </section>
          <section>
            <h2>Чаты</h2>
            <SkelTable title="Аккаунты — первые контакты" />
            <SkelTable title="Продажники — скорость ответа" />
          </section>
          <section>
            <h2>Продажи</h2>
            <SkelTable title="Рынки" />
            <SkelTable title="Продажники" />
          </section>
        </div>
      ) : (
      <div className="pulse-details">
        <section>
          <h2>Meta Ads</h2>
          <Table
            title="Кампании — лиды"
            rows={meta.campaigns}
            cols={[
              { label: "Кампания", render: (r) => r.label },
              { label: "Лиды к часу / норма", render: (r) => vsNorm(r), num: true },
              { label: "Лиды за день", render: (r) => fmt(r.value), num: true },
              { label: "Клики", render: (r) => fmt(r.extra?.clicks), num: true },
              { label: `Спенд, ${cur}`, render: (r) => fmt(r.extra?.spend, 2), num: true },
              { label: "Цена лида", render: (r) => fmt(r.extra?.cpl, 2), num: true },
            ]}
          />
          <Table
            title="Креативы — лиды за календарные сутки против своей нормы (клик — графики за неделю)"
            rows={meta.creatives.slice(0, 15)}
            empty={errors.creatives || "Нет данных"}
            onRowClick={meta.creativeDays ? (r) => setOpenCreative(r.key) : undefined}
            cols={[
              {
                label: "Креатив",
                render: (r) => (
                  <div className="pulse-creative">
                    {r.extra?.thumb ? (
                      <img className="pulse-creative__thumb" src={r.extra.thumb} alt="" loading="lazy" />
                    ) : (
                      <span className="pulse-creative__thumb" />
                    )}
                    <div>
                      {r.label}
                      {r.extra?.isVideo && <span className="pulse-tag">видео</span>}
                      {r.extra?.hot && <span className="pulse-tag pulse-tag--hot">стрельнул</span>}
                      {r.extra?.isNew && <span className="pulse-tag">новый</span>}
                      <div className="pulse-muted pulse-small">{r.extra?.campaign}</div>
                    </div>
                  </div>
                ),
              },
              { label: "Лиды / норма", render: (r) => vsNorm(r, 0), num: true },
              { label: "Клики", render: (r) => fmt(r.extra?.clicks), num: true },
              { label: `Спенд, ${cur}`, render: (r) => fmt(r.extra?.spend, 2), num: true },
              { label: "Цена лида", render: (r) => fmt(r.extra?.cpl, 2), num: true },
            ]}
          />
        </section>

        <section>
          <h2>Чаты</h2>
          <Table
            title="Аккаунты — первые контакты"
            rows={chats.channels}
            empty={errors.chats || "Нет данных"}
            cols={[
              {
                label: "Аккаунт",
                render: (r) => r.label,
              },
              { label: "Новые к часу / норма", render: (r) => vsNorm(r), num: true },
              { label: "Входящие", render: (r) => fmt(r.extra?.inbound), num: true },
              { label: "Ответов людьми", render: (r) => fmt(r.extra?.outboundHuman), num: true },
              { label: "Новые без ответа", render: (r) => fmt(r.extra?.unansweredNew), num: true },
            ]}
          />
          <Table
            title={`Продажники — скорость ответа${data.chatsCalendarDays ? " (по календарным суткам)" : ""}`}
            rows={chats.operators}
            empty={errors.chats || "Нет данных"}
            cols={[
              {
                label: "Продажник",
                render: (r) => (
                  <>
                    {r.label}
                    {r.extra?.absent && <span className="pulse-tag pulse-tag--bad">не работает</span>}
                  </>
                ),
              },
              { label: "Ответов / в среднем", render: (r) => <>{fmt(r.value)} <span className="pulse-muted">/ {fmt(r.norm)}</span></>, num: true },
              { label: "Медиана, мин", render: (r) => fmt(r.extra?.medianMin, 1), num: true },
              { label: "Среднее до 2 ч, мин", render: (r) => fmt(r.extra?.avgMinUpTo2h, 1), num: true },
              { label: "Ответ за 5 мин", render: (r) => (r.extra?.pctWithin5Min === null || r.extra?.pctWithin5Min === undefined ? "—" : `${fmt(r.extra.pctWithin5Min)}%`), num: true },
            ]}
          />
        </section>

        <section>
          <h2>Продажи</h2>
          <div className="pulse-muted pulse-small">
            Предоплаты и полные оплаты, без возвратов и архива; сутки {clock(start)}–{clock(start)} Алматы. Продажники — только роль sales.
          </div>
          <Table
            title="Рынки"
            rows={sales.markets}
            empty={errors.sales || "Нет продаж"}
            cols={[
              { label: "Рынок", render: (r) => r.label },
              { label: "К часу / норма", render: (r) => vsNorm(r), num: true },
              { label: "За день", render: (r) => fmt(r.value), num: true },
            ]}
          />
          <Table
            title="Продажники"
            rows={sales.managers}
            empty={errors.sales || "Нет продаж"}
            cols={[
              { label: "Продажник", render: (r) => r.label },
              { label: "К часу / норма", render: (r) => vsNorm(r), num: true },
              { label: "За день", render: (r) => fmt(r.value), num: true },
            ]}
          />
          {sales.channels && (
            <Table
              title="Чаты — купили из первых контактов дня (тег «Купил»)"
              rows={sales.channels}
              cols={[
                {
                  label: "Аккаунт",
                  render: (r) => r.label,
                },
                { label: "Первые контакты", render: (r) => fmt(r.extra?.newContacts), num: true },
                { label: "Купили / норма", render: (r) => <>{fmt(r.value)} <span className="pulse-muted">/ {fmt(r.norm)}</span></>, num: true },
                {
                  label: "Конверсия / норма",
                  render: (r) => (
                    <>
                      {r.extra?.conv === null ? "—" : `${fmt(r.extra?.conv, 1)}%`}{" "}
                      <span className="pulse-muted">/ {r.extra?.convNorm === null ? "—" : `${fmt(r.extra?.convNorm, 1)}%`}</span>
                    </>
                  ),
                  num: true,
                },
              ]}
            />
          )}
        </section>
      </div>
      )}
      {ready && openCreative && (() => {
        const c = meta.creatives.find((r) => r.key === openCreative);
        const days = meta.creativeDays?.[openCreative];
        if (!c || !days) return null;
        return (
          <CreativeModal
            label={c.label}
            campaign={c.extra?.campaign || ""}
            thumb={c.extra?.thumb}
            previewLink={c.extra?.previewLink}
            isVideo={c.extra?.isVideo}
            currency={cur}
            isToday={sel.isToday}
            days={days}
            onClose={() => setOpenCreative(null)}
          />
        );
      })()}
    </div>
  );
};

export default PulsePage;
